import "../index.css";
import { useEffect } from "react";
import Footer from "../components/Footer";
//import { useAuth } from "../auth/context";
import { PageNav } from "../components/PageNav";
import ReactMarkdown from "react-markdown";

export default function Privacy() {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const markdownContent = `
# Privacy Policy

**Last Updated: October 18, 2026**

## 1. Introduction

This Privacy Policy describes how StakeAlerts LLC ("we," "us," or "our") collects, uses, and shares information about you when you use our mobile application and website (collectively, the "Service"). 

**What We Do:** StakeAlerts is a sports betting information and alert service. We aggregate real-time odds and betting lines from various sportsbooks (including DraftKings, FanDuel, and others) and allow you to set custom alerts based on your preferences. We are NOT a gambling site and do not process any bets. We simply provide information and notifications about betting opportunities.

**Age Requirement:** Our Service is intended for adults aged 18 years or older (21+ where required by law for sports betting content). We do not knowingly collect information from individuals under the applicable age requirement.

By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.

## 2. Information We Collect

### 2.1 Information You Provide to Us

**Account Information:** When you create an account, we collect:
- Email address
- Password (encrypted, only if you register with email/password)
- Authentication information from third-party providers (Google Sign-In or Apple Sign-In, if you choose these options)
- Date of birth (to verify age requirement)

**Subscription and Payment Information:** 
- For web subscriptions: Payment is processed through Stripe. We do not store your full payment card information; Stripe handles all payment data securely.
- For mobile subscriptions: Payment is processed through Apple App Store or Google Play Store. We receive only confirmation of your subscription status, not your payment information.

**Alert Rules and Preferences:** You can create custom alert rules, which we store:
- Sports and teams you want to monitor
- Betting markets and conditions (e.g., "MLB games where over/under > 20")
- Sportsbooks you want to track
- Alert delivery preferences (push notifications, SMS)
- Phone number (if you opt in to receive SMS alerts)

**Communications:** When you contact us, we collect:
- The content of your messages
- Any information you choose to provide

### 2.2 Information Collected Automatically

**Device Information:**
- Device type and model
- Operating system and version
- Unique device identifiers, including the advertising identifier on Android and the vendor identifier on iOS
- Mobile network information

**App Install and Marketing Attribution Information:**
- How you found and installed our app (for example, which advertisement, campaign, or website referred you)
- App installs, opens, sign-ups, and subscription events (such as a trial starting or a subscription renewing)

**Crash and Error Information:**
- Crash reports and error details, including the device and app version and your account's user ID, so we can find and fix problems

**Usage Information:**
- Alert rules you create and modify
- Games and betting lines you view
- Sports and teams you monitor
- Time and duration of activities within the Service
- Features you use (live game monitoring, alert management, etc.)
- Which sportsbooks' odds you view

**Location Information:**
- General location based on IP address (to comply with regional sports betting information regulations)

**Cookies and Similar Technologies:**
- We use cookies, web beacons, and similar technologies to collect information about your browsing activities

## 3. How We Use Your Information

We use the information we collect to:

### 3.1 Provide and Improve Our Service
- Create and manage your account (via email/password, Google Sign-In, or Apple Sign-In)
- Process your subscription payments
- Deliver real-time odds and betting line information from sportsbooks
- Process and deliver alerts based on your custom rules (push notifications and SMS)
- Allow you to monitor live games and betting opportunities
- Customize content based on your sports, teams, and alert preferences
- Analyze and improve Service functionality
- Diagnose crashes and fix errors
- Develop new features and alerting capabilities

### 3.2 Communicate With You
- Send subscription confirmations and payment receipts
- Provide customer support
- Send important Service updates and announcements
- Send promotional communications (with your consent, where required)
- Respond to your inquiries

### 3.3 Safety and Security
- Verify your age and identity
- Prevent fraud and abuse
- Comply with legal obligations
- Enforce our Terms of Service
- Protect our rights and property

### 3.4 Analytics and Research
- Understand how users interact with our Service
- Measure which of our advertising and marketing campaigns lead people to install and subscribe to our app
- Conduct market research
- Create aggregated, de-identified data for analysis

## 4. How We Share Your Information

**We do not sell your personal information to anyone.** We do not share your information with sportsbooks, and we do not allow third parties to use your information for their own marketing purposes.

We may share your information only in the following limited circumstances:

### 4.1 Third-Party Service Providers

We share information with the following vendors who perform services on our behalf:

**Authentication & Database:**
- Firebase (Google) - for user authentication and data storage

**Payment Processing:**
- Stripe - for web subscription payments
- RevenueCat - to manage in-app subscriptions purchased through the Apple App Store or Google Play Store (RevenueCat receives your subscription and purchase status and your account's user ID, not your payment card information)
- Apple App Store - for iOS subscription payments (Apple receives payment information, we only receive subscription status)
- Google Play Store - for Android subscription payments (Google receives payment information, we only receive subscription status)

**Analytics and Attribution:**
- Google Analytics - to understand how users interact with our Service
- AppsFlyer - to measure how people find and install our app and which of our marketing campaigns lead to installs and subscriptions. AppsFlyer receives device identifiers, app install and usage events, subscription events, and your account's user ID

**Crash Reporting:**
- Sentry - to receive crash reports and error details so we can fix problems in the app

**Communication Services:**
- SMS service providers - to deliver text message alerts (only if you opt in to SMS alerts)
- Push notification services (Apple Push Notification Service, Firebase Cloud Messaging) - to deliver in-app alerts

These service providers are contractually obligated to protect your information and use it only for the purposes we specify.

### 4.2 Sportsbook Data Sources

**Important:** We do NOT share your personal information, alert rules, or betting preferences with any sportsbooks.

We retrieve publicly available odds and betting line information from sportsbooks (DraftKings, FanDuel, and others) to power our Service. This is a one-way data flow - we collect their public odds information, but we do not send them any information about you.

We have no affiliate relationships, partnerships, or business arrangements with any sportsbooks.

### 4.3 Business Transfers

If we are involved in a merger, acquisition, sale of assets, or bankruptcy, your information may be transferred as part of that transaction.

### 4.4 Legal Requirements

We may disclose your information if required by law or in response to:
- Court orders or subpoenas
- Government or regulatory requests
- Requests from law enforcement
- Protection of our legal rights
- Prevention of fraud or security threats

### 4.5 With Your Consent

We may share your information with third parties when you give us permission to do so.

## 5. Third-Party Links and Services

Our Service displays odds and information from various sportsbooks but does not contain direct links to place bets. If you choose to visit a sportsbook website to place a bet based on information from our Service, you will be subject to that sportsbook's privacy policy and terms of service.

**Third-Party Authentication:** If you sign in using Google Sign-In or Apple Sign-In, those services' privacy policies apply to the authentication process. We receive only basic profile information (email address) from these services.

We are not responsible for the privacy practices of sportsbooks or other third-party services.

## 6. Data Retention

We retain your information for as long as necessary to:
- Provide our Service to you
- Comply with legal obligations
- Resolve disputes
- Enforce our agreements

When you cancel your subscription or delete your account, we will delete or anonymize your information within 90 days, except where we must retain it for legal, accounting, or security purposes.

## 7. Your Privacy Rights

Depending on your location, you may have certain rights regarding your personal information:

### 7.1 Access and Portability
- Request a copy of your personal information
- Receive your data in a portable format

### 7.2 Correction and Update
- Update or correct inaccurate information
- Access your account settings to modify your profile

### 7.3 Deletion
- Request deletion of your account and personal information
- Note: Some information may be retained as required by law

### 7.4 Opt-Out Rights
- Unsubscribe from marketing emails (via the link in each email)
- Disable push notifications (in app settings)
- Reset or delete your device's advertising identifier (Android: Settings > Google > Ads; iOS apps cannot access the advertising identifier unless you allow tracking)
- Opt out of certain cookies (in browser settings)
- Request that we do not sell your information (we do not sell personal information)

### 7.5 California Privacy Rights (CCPA/CPRA)

If you are a California resident, you have additional rights:
- Right to know what personal information we collect
- Right to know if we sell or share personal information (we do not)
- Right to request deletion of personal information
- Right to correct inaccurate personal information
- Right to limit use of sensitive personal information
- Right to non-discrimination for exercising your rights

### 7.6 European Privacy Rights (GDPR)

If you are in the European Economic Area, UK, or Switzerland, you have rights including:
- Right to access your personal data
- Right to rectification of inaccurate data
- Right to erasure ("right to be forgotten")
- Right to restrict processing
- Right to data portability
- Right to object to processing
- Right to withdraw consent
- Right to lodge a complaint with a supervisory authority

### 7.7 Exercising Your Rights

To exercise any of these rights, please contact us at support@stakealerts.com. We will respond to your request within the timeframe required by applicable law (typically 30-45 days).

## 8. International Data Transfers

If you are located outside the United States, your information may be transferred to and processed in the United States or other countries where our service providers operate. These countries may have different data protection laws than your country.

We take appropriate safeguards to ensure your information remains protected, including:
- Standard Contractual Clauses
- Adequacy decisions
- Other lawful transfer mechanisms

## 9. Children's Privacy

Our Service is not intended for anyone under 18 years of age (or 21+ where required by law for sports betting content). We do not knowingly collect information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will delete such information.

## 10. Security

We implement reasonable security measures to protect your information, including:
- Encryption of data in transit (SSL/TLS)
- Encryption of sensitive data at rest
- Regular security assessments
- Access controls and authentication
- Secure payment processing through PCI-DSS compliant providers

However, no method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.

## 11. Do Not Track

Some browsers have "Do Not Track" features that allow you to tell websites not to track you. We currently do not respond to Do Not Track signals, but we do provide you with opt-out options as described in this Privacy Policy.

## 12. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. When we make material changes, we will:
- Update the "Last Updated" date at the top of this policy
- Notify you via email or through the Service
- Require your acceptance if required by law

Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.

## 13. Contact Us

If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:

**Email:** support@stakealerts.com

**Company Name:** StakeAlerts LLC

For data deletion requests, privacy inquiries, or to exercise your privacy rights, please email us at support@stakealerts.com with "Privacy Request" in the subject line.

## 14. Additional State-Specific Rights

### Nevada Residents
Nevada residents may opt out of the sale of covered personal information. We do not sell personal information as defined under Nevada law. However, if you wish to submit an opt-out request, you may contact us at the email address above.

### Virginia, Colorado, Connecticut, and Utah Residents
Residents of these states have rights similar to those described in Section 7, including rights to access, correct, delete, and opt out of targeted advertising and the sale of personal information (we do not engage in these activities).

---

**Your Consent**

By using our Service, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.`;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          className="terms-container"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "2rem",
            lineHeight: "1.6",
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({ ...props }) => (
                <h1
                  className="font_family_base"
                  style={{
                    fontSize: "2.5rem",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #737373",
                    paddingBottom: "0.5rem",
                  }}
                  {...props}
                />
              ),
              h2: ({ ...props }) => (
                <h2
                  className="font_family_base header_text"
                  style={{
                    fontSize: "1.8rem",
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    // color: "#00bf62",
                  }}
                  {...props}
                />
              ),
              h3: ({ ...props }) => (
                <h3
                  className="font_family_base detail_text"
                  style={{
                    fontSize: "1.4rem",
                    marginTop: "1.5rem",
                    marginBottom: "0.75rem",
                  }}
                  {...props}
                />
              ),
              p: ({ ...props }) => (
                <p
                  className="font_family_base detail_text"
                  style={{ marginBottom: "1rem" }}
                  {...props}
                />
              ),
              ul: ({ ...props }) => (
                <ul
                  className="font_family_base detail_text"
                  style={{ marginLeft: "2rem", marginBottom: "1rem" }}
                  {...props}
                />
              ),
              li: ({ ...props }) => (
                <li
                  className="font_family_base detail_text"
                  style={{ marginBottom: "0.5rem" }}
                  {...props}
                />
              ),
              strong: ({ ...props }) => (
                <strong
                  className="font_family_base header_text"
                  // style={{ color: "#00bf62" }}
                  {...props}
                />
              ),
              hr: ({ ...props }) => (
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #737373",
                    margin: "2rem 0",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
      <Footer />
    </div>
  );
}
